import React, { useMemo, useState } from 'react';
import { Modal, View, Text, StyleSheet } from 'react-native';
import { Checkbox, Button as PaperButton } from 'react-native-paper';
import { GTFonts, useTheme } from '../../theme';
import { fs, s, v } from '../../utils/libs';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { AppSnackbar } from '../../services/AppSnackbar';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import type { PoSendChannel } from '../../services/appAnalyticsTypes';
import ReviewService from '../../services/ReviewService';
import { sendPurchaseOrderToSupplier } from '../../api/backofficeSuppliers';

type Props = {
  visible: boolean;
  purchaseOrderId: string;
  mode?: 'send' | 'resend';
  onClose: () => void;
  onSuccess?: (response: any) => void;
};

const SendPOModal: React.FC<Props> = ({
  visible,
  purchaseOrderId,
  mode = 'send',
  onClose,
  onSuccess,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();

  const [sendEmail, setSendEmail] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [sending, setSending] = useState(false);

  const isResend = mode === 'resend';
  const canSubmit = (sendEmail || sendSms) && !sending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!purchaseOrderId || !owner?.accessToken) return;

    const channels: string[] = [];
    if (sendEmail) channels.push('email');
    if (sendSms) channels.push('sms');

    setSending(true);
    try {
      const res = await sendPurchaseOrderToSupplier(
        axiosBackoffice as any,
        { purchaseOrderId, channels },
        { accessToken: owner.accessToken },
      );
      const ok = res?.data?.success === true || res?.data?.status === 200;
      const message = String(
        res?.data?.message ||
          (ok
            ? 'Purchase order sent to supplier successfully'
            : 'Failed to send purchase order'),
      );
      AppSnackbar.show(message, ok ? 'success' : 'error');
      if (!ok) return;
      for (const channel of channels) {
        if (channel === 'email' || channel === 'sms') {
          appAnalytics.track('procurement_po_sent', {
            ...buildAppAnalyticsContext(),
            channel: channel as PoSendChannel,
          });
        }
      }
      ReviewService.onEligibleAction('PO_SENT');
      onSuccess?.(res);
      onClose();
    } catch (error: any) {
      AppSnackbar.show(
        error?.message || 'Failed to send purchase order',
        'error',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>
            {isResend
              ? 'Resend Purchase Order to Supplier'
              : 'Send Purchase Order to Supplier'}
          </Text>
          <Text style={styles.subtext}>
            Choose at least one channel. The supplier receives the order via the selected channel.
          </Text>

          <View style={styles.checkboxRow}>
            <Checkbox.Android
              status={sendEmail ? 'checked' : 'unchecked'}
              onPress={() => setSendEmail(prev => !prev)}
            />
            <Text style={styles.checkboxLabel}>Email</Text>
          </View>

          <View style={styles.checkboxRow}>
            <Checkbox.Android
              status={sendSms ? 'checked' : 'unchecked'}
              onPress={() => setSendSms(prev => !prev)}
            />
            <Text style={styles.checkboxLabel}>SMS</Text>
          </View>

          <View style={styles.checkboxRow}>
            <Checkbox.Android status="unchecked" disabled />
            <Text style={styles.checkboxLabelDisabled}>
              WhatsApp (coming soon)
            </Text>
          </View>

          <View style={styles.actions}>
            <PaperButton
              mode="outlined"
              onPress={onClose}
              disabled={sending}
              style={styles.actionButton}
              labelStyle={styles.actionLabel}>
              Cancel
            </PaperButton>
            <PaperButton
              mode="contained"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={sending}
              style={styles.actionButton}
              labelStyle={styles.actionLabel}>
              {sending ? 'Sending...' : isResend ? 'Resend' : 'Send'}
            </PaperButton>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.45)',
      padding: s(16),
    },
    card: {
      width: '100%',
      maxWidth: s(480),
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: s(16),
      ...theme.shadows.md,
    },
    title: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: theme.colors.text,
      marginBottom: v(4),
    },
    subtext: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      marginBottom: v(10),
    },
    checkboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: v(4),
    },
    checkboxLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    checkboxLabelDisabled: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.textTertiary,
    },
    actions: {
      marginTop: v(10),
      gap: v(8),
    },
    actionButton: {
      borderRadius: theme.borderRadius.md,
    },
    actionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
  });

export default SendPOModal;
